/**
 * Admin Login Component
 * Secure login page for admin dashboard
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import '../assets/css/admin-portal.css';

function AdminLogin() {
  const navigate = useNavigate();
  const { isAuthenticated, loading, login } = useAdmin();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/app-admin/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    if (!email || !password) {
      setError('Please enter both email and password');
      setIsSubmitting(false);
      return;
    }

    const result = await login(email, password);

    if (result.success) {
      navigate('/app-admin/dashboard');
    } else {
      setError(result.error);
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-auth-page">
        <div className="admin-auth-container">
          <div className="admin-auth-card">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="admin-spinner" style={{ width: 40, height: 40 }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-auth-page">
      <div className="admin-auth-container">
        <div className="admin-auth-card">
          <div className="admin-auth-header">
            <Link to="/">
              <img
                src={require('../assets/images/Y.png')}
                alt="Yemberzal"
                className="admin-auth-logo"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/default-image.avif';
                }}
              />
            </Link>
            <h1 className="admin-auth-title">Admin Dashboard</h1>
            <p className="admin-auth-subtitle">
              Secure access for platform administrators
            </p>
          </div>

          {error && (
            <div className="admin-alert admin-alert-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="admin-form-group">
              <label className="admin-form-label">Email Address</label>
              <input
                type="email"
                className={`admin-form-input ${error ? 'error' : ''}`}
                placeholder="admin@theyemberzal.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Password</label>
              <input
                type="password"
                className={`admin-form-input ${error ? 'error' : ''}`}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="admin-spinner" />
                  Signing in...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                    <polyline points="10 17 15 12 10 7"/>
                    <line x1="15" y1="12" x2="3" y2="12"/>
                  </svg>
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="admin-auth-footer">
            <Link to="/" className="admin-btn-link">
              ← Back to Website
            </Link>
          </div>
        </div>

        <div className="admin-security-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          Restricted Access - Authorized Personnel Only
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
